import { SEED_THEME } from '@/lib/seed-theme';
import { createStep, emptyRoot, type AuthoringDoc, type Step } from '@personalizehq/editor';
import { DOC_VERSION } from '@personalizehq/schemas';
import type { Theme } from '@personalizehq/schemas/ir';
import bannerTop from './banner_top.json';
import bottomSheetConfirm from './bottom_sheet_confirm.json';
import fullscreenCarousel from './fullscreen_carousel_3step.json';
import inlineStrip from './inline_strip.json';
import modalBasic from './modal_basic.json';
import storySlide from './story_slide.json';

// The gallery. These are NEW curated documents, not fixtures: the fixture corpus is adversarial
// (`modal_adversarial`, `containers_adversarial`) — it exists to break the solver, not to start a
// design from. Zero renderer code either way; a template is data.
//
// ponytail: a template carries STEPS, not a theme. The spec has templates carrying their own theme
// "re-bound to the app theme's token names where they match" — but they are authored against the
// seed token names, so binding them to the app theme IS that re-bind, minus a diffing pass nobody
// needs yet. A template that used a token the app theme lacks would surface as an inline "not a
// token in this theme" on the field, which is the behaviour we want anyway.

export interface Template {
  id: string;
  name: string;
  description: string;
  steps: Step[];
  family: 'overlay' | 'embedded';
  pattern: string;
  outcomes: Outcome[];
  complexity: 'Simple' | 'Multi-step' | 'Advanced';
  prerequisites: string[];
  blank?: boolean;
}

export const OUTCOMES = [
  'Onboard & activate',
  'Announce & educate',
  'Promote & convert',
  'Guide & support',
] as const;

export type Outcome = (typeof OUTCOMES)[number];

export const TEMPLATES: Template[] = [
  {
    id: 'modal_basic',
    ...(modalBasic as Pick<Template, 'steps'>),
    name: 'Simple announcement',
    description: 'A focused card with image, headline, supporting copy, and one clear action.',
    family: 'overlay',
    pattern: 'Announcement card',
    outcomes: ['Announce & educate', 'Promote & convert'],
    complexity: 'Simple',
    prerequisites: [],
  },
  {
    id: 'fullscreen_carousel_3step',
    ...(fullscreenCarousel as Pick<Template, 'steps'>),
    name: 'Three-step onboarding',
    description: 'A full-screen introduction with three ordered steps, progress, and next actions.',
    family: 'overlay',
    pattern: 'Onboarding flow',
    outcomes: ['Onboard & activate', 'Guide & support'],
    complexity: 'Multi-step',
    prerequisites: [],
  },
  {
    id: 'bottom_sheet_confirm',
    ...(bottomSheetConfirm as Pick<Template, 'steps'>),
    name: 'Confirm an action',
    description: 'A bottom sheet that explains one decision and offers confirm or dismiss actions.',
    family: 'overlay',
    pattern: 'Decision prompt',
    outcomes: ['Guide & support', 'Promote & convert'],
    complexity: 'Simple',
    prerequisites: [],
  },
  {
    id: 'banner_top',
    ...(bannerTop as Pick<Template, 'steps'>),
    name: 'Top announcement',
    description: 'A compact top banner for a timely update, offer, or release message.',
    family: 'overlay',
    pattern: 'Banner notice',
    outcomes: ['Announce & educate', 'Promote & convert'],
    complexity: 'Simple',
    prerequisites: [],
  },
  {
    id: 'story_slide',
    ...(storySlide as Pick<Template, 'steps'>),
    name: 'Full-screen story',
    description: 'An immersive media-first story with overlaid copy and segmented progress.',
    family: 'overlay',
    pattern: 'Story',
    outcomes: ['Announce & educate', 'Promote & convert'],
    complexity: 'Simple',
    prerequisites: ['Media recommended'],
  },
  // F10 — the gallery had NO `inline` template, though `inline` is pinned v1 item 12 and the hardest
  // surface to author by hand (a placement, a slot, exactly one step, a snapping scroll, tracking
  // dots that reconcile `segments`). This is the pinned slice-gate artefact (`inline-strip.test.ts`)
  // as a starting point authors can open.
  {
    id: 'inline_strip',
    ...(inlineStrip as Pick<Template, 'steps'>),
    name: 'Auto-advancing content carousel',
    description: 'A snapping carousel that rotates inside a host-app slot and keeps its page dots in sync.',
    family: 'embedded',
    pattern: 'Content carousel',
    outcomes: ['Announce & educate', 'Promote & convert'],
    complexity: 'Advanced',
    prerequisites: ['Named host slot', 'Inline campaign placement'],
  },
];

function blankStep(surface: Step['surface']): Step {
  const root = emptyRoot();
  root.size = surface === 'fullscreen'
    ? { width: 'fill', height: 'fill' }
    : { width: 'fill' };
  return createStep({ name: 'Step 1', surface, root });
}

export const BLANK_TEMPLATES: Template[] = [
  ['blank_modal', 'Blank centered modal', 'modal', 'Centered modal'],
  ['blank_fullscreen', 'Blank full-screen', 'fullscreen', 'Full-screen'],
  ['blank_bottom_sheet', 'Blank bottom sheet', 'bottom_sheet', 'Bottom sheet'],
  ['blank_banner', 'Blank banner', 'banner', 'Banner'],
  ['blank_inline', 'Blank inline content', 'inline', 'Inline content'],
].map(([id, name, surface, pattern]) => ({
  id,
  name,
  description:
    surface === 'inline'
      ? 'Start with one valid embedded step. Delivery requires a named slot in the host app.'
      : `Start with an empty, valid ${String(pattern).toLowerCase()} and build the structure yourself.`,
  steps: [blankStep(surface as Step['surface'])],
  family: surface === 'inline' ? 'embedded' : 'overlay',
  pattern,
  outcomes: [...OUTCOMES],
  complexity: surface === 'inline' ? 'Advanced' : 'Simple',
  prerequisites: surface === 'inline' ? ['Named host slot', 'Inline campaign placement'] : [],
  blank: true,
})) as Template[];

export const STARTERS = [...TEMPLATES, ...BLANK_TEMPLATES];

/** Clone a template into a fresh draft, bound to the app's theme (or the seed, if the app has never
 *  written one — the API stores NULL until it does). The clone is deep: the module's imported JSON
 *  is shared across every "new experience" click. */
export function templateDoc(template: Template, theme: Theme | null | undefined): AuthoringDoc {
  return {
    doc_version: DOC_VERSION,
    theme: theme ?? SEED_THEME,
    steps: structuredClone(template.steps),
  };
}
